import { authenticatedRequest, StudentAssessmentApiError } from "./studentAssessmentApi";
import { normalizeDrawing } from "./drawingFormat";

// Client for /api/quiz-drawings/*. Every request names the attempt and
// question; object paths and image URLs never come from the browser.

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const validRevision = (value) => Number.isSafeInteger(value) && value >= 0;

const invalidResponse = () =>
  new StudentAssessmentApiError("The server returned an invalid drawing response.", 502, "invalid-response");

// Network failures are reported as status 0 / "network-error" so callers can
// tell "not confirmed" apart from a refusal.
const send = async (path, body) => {
  let response;
  try {
    response = await authenticatedRequest(path, { method: "POST", body: JSON.stringify(body) });
  } catch (error) {
    if (error instanceof StudentAssessmentApiError) throw error;
    throw new StudentAssessmentApiError("We couldn't reach StudyDen. Check your connection.", 0, "network-error");
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new StudentAssessmentApiError(
      data.error || "The drawing request could not be completed.",
      response.status,
      data.code || "drawing-unavailable"
    );
    error.details = {
      drawing: isPlainObject(data.drawing) ? normalizeSummary(data.drawing) : null,
      reason: typeof data.reason === "string" ? data.reason : null,
    };
    throw error;
  }
  const data = await response.json().catch(() => null);
  if (!isPlainObject(data) || data.ok !== true) throw invalidResponse();
  return data;
};

const isoOrNull = (value) => (typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : null);

export function normalizeSummary(value) {
  if (!isPlainObject(value) || !validRevision(value.revision) || typeof value.hasDrawing !== "boolean") throw invalidResponse();
  return {
    revision: value.revision,
    hasDrawing: value.hasDrawing,
    committedAt: isoOrNull(value.committedAt),
    draft: isPlainObject(value.draft) ? {
      editSessionId: String(value.draft.editSessionId || ""),
      sequence: Number.isSafeInteger(value.draft.sequence) ? value.draft.sequence : 0,
      acceptedAt: isoOrNull(value.draft.acceptedAt),
      blank: value.draft.blank === true,
    } : null,
    editing: isPlainObject(value.editing) ? {
      editSessionId: String(value.editing.editSessionId || ""),
      openedAt: isoOrNull(value.editing.openedAt),
    } : null,
  };
}

export const fetchDrawingState = async (attemptId) => {
  const data = await send("/api/quiz-drawings/state", { attemptId });
  if (typeof data.enabled !== "boolean" || !isPlainObject(data.drawings)) throw invalidResponse();
  return {
    enabled: data.enabled,
    drawings: Object.fromEntries(Object.entries(data.drawings).map(([questionId, summary]) => [questionId, normalizeSummary(summary)])),
  };
};

export const openDrawing = async ({ attemptId, questionId, editSessionId, takeover = false }) => {
  const data = await send("/api/quiz-drawings/open", { attemptId, questionId, editSessionId, takeover });
  if (data.editSessionId !== editSessionId || !validRevision(data.revision) || !validRevision(data.acceptedSequence)) {
    throw invalidResponse();
  }
  return {
    resumed: data.resumed === true,
    tookOver: data.tookOver === true,
    revision: data.revision,
    acceptedSequence: data.acceptedSequence,
    committed: data.committed ? { drawing: normalizeDrawing(data.committed.drawing), committedAt: isoOrNull(data.committed.committedAt) } : null,
    draft: data.draft ? {
      drawing: normalizeDrawing(data.draft.drawing),
      acceptedAt: isoOrNull(data.draft.acceptedAt),
      sequence: data.draft.sequence,
      blank: data.draft.blank === true,
    } : null,
  };
};

export const checkpointDrawing = async ({ attemptId, questionId, editSessionId, sequence, drawing }) => {
  const data = await send("/api/quiz-drawings/checkpoint", { attemptId, questionId, editSessionId, sequence, drawing });
  if (!["saved", "superseded"].includes(data.status) || !validRevision(data.acceptedSequence)) throw invalidResponse();
  return { status: data.status, acceptedSequence: data.acceptedSequence, acceptedAt: isoOrNull(data.acceptedAt) };
};

export const commitDrawing = async ({ attemptId, questionId, editSessionId, requestId, baseRevision, drawing }) => {
  const data = await send("/api/quiz-drawings/commit", { attemptId, questionId, editSessionId, requestId, baseRevision, drawing });
  if (data.status !== "saved" || !validRevision(data.revision)) throw invalidResponse();
  return { status: data.status, revision: data.revision };
};

export const cancelDrawing = async ({ attemptId, questionId, editSessionId, requestId }) => {
  const data = await send("/api/quiz-drawings/cancel", { attemptId, questionId, editSessionId, requestId });
  if (!["cancelled", "session-closed"].includes(data.status) || !validRevision(data.revision)) throw invalidResponse();
  return { status: data.status, reason: typeof data.reason === "string" ? data.reason : null, revision: data.revision };
};

export const removeDrawing = async ({ attemptId, questionId, requestId, baseRevision }) => {
  const data = await send("/api/quiz-drawings/remove", { attemptId, questionId, requestId, baseRevision });
  if (data.status !== "removed" || !validRevision(data.revision)) throw invalidResponse();
  return { status: data.status, revision: data.revision };
};

export const fetchDrawingImage = async ({ attemptId, questionId, version }) => {
  const response = await authenticatedRequest("/api/quiz-drawings/evidence", {
    method: "POST",
    body: JSON.stringify({ attemptId, questionId, version }),
  });
  if (!response.ok) throw new StudentAssessmentApiError("The drawing could not be loaded.", response.status, "drawing-evidence-unavailable");
  const blob = await response.blob();
  if (blob.type !== "image/png") throw invalidResponse();
  return blob;
};

// Client-generated identities for edit sessions and requests.
export const newClientId = () => {
  const bytes = new Uint8Array(18);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};
